/**
 * Medida de una capa 2D (DWG/DXF) para la pantalla de planos — paso 3 del plan de medición DWG: "una
 * sola medición". La DECISIÓN de cómo medir la capa (¿es un muro de doble trazo? ¿qué área encierra y
 * qué tan fiable es?) la toma el lector de planos (`plano2svg.py`, `cuantificacion.fuente` y
 * `cuantificacion.superficie`) con los umbrales de Calibración. Acá solo se aplica esa decisión con el
 * factor de escala vigente en la pantalla (que puede ser una unidad elegida por el usuario), sin
 * umbrales propios: antes la pantalla recalculaba con sus propias constantes y, para un piso tomado de
 * la capa de muros, ofrecía el área aproximada en lugar de la de los recintos cerrados.
 *
 * Una sola función para la capa completa y para cada vista (planta), así las dos dan lo mismo por el
 * mismo camino.
 */
import type { PlanoCuantificacion } from './domain.js';

/** Lo que la función lee de una capa (o de una capa dentro de una vista), en unidades del dibujo. */
export interface CapaDwgParaMedir {
  longitud?: number;
  area?: number;
  area_recintos?: number;
  corrido?: number;
  espesor?: number | null;
  recintos_medidos?: { medidos: { n: number; area_m2: number } } | null;
  cuantificacion?: PlanoCuantificacion;
}

export interface MedidaDeCapaDwg {
  conEscala: boolean;
  longitudUnid: number;
  /** Largo crudo del trazo en metros (cuenta las dos caras de un muro). */
  metros: number | null;
  /** Mejor área de la capa en m², según el lector. */
  areaM2: number | undefined;
  areaEsAprox: boolean;
  /** La capa se mide por el eje del muro de doble trazo. */
  dobleTrazo: boolean;
  corridoM: number | null;
  espesorSugerido: number | undefined;
  /** Hay que revisarla: patrón de muro poco nítido, o área solo aproximada. */
  revisar: boolean;
  /** El largo que se ofrece: el eje del muro si lo es, si no el largo crudo. */
  baseLargo: number | null;
}

const r2 = (x: number) => Math.round(x * 100) / 100;
const r3 = (x: number) => Math.round(x * 1000) / 1000;
const valido = (f: number | null | undefined): f is number => f != null && Number.isFinite(f) && f > 0;

/**
 * @param m capa (o capa en una vista) del inventario.
 * @param factor factor unidad del dibujo → metro vigente en la pantalla (null = sin escala).
 * @param esMuroSemantico la clasificación de capas (IA) no la excluye de muro.
 * @param factorDelLector el factor con que midió el lector (`inventario.escala.factor`). Los recintos
 *        cerrados vienen ya en m² con ese factor; si la pantalla usa otro, se reescalan.
 */
export function medidaDeCapaDwg(
  m: CapaDwgParaMedir,
  factor: number | null,
  esMuroSemantico: boolean,
  factorDelLector?: number | null,
): MedidaDeCapaDwg {
  const conEscala = valido(factor);
  const f = conEscala ? factor : 0;
  const longitudUnid = m.longitud ?? 0;
  const metros = conEscala && longitudUnid > 0 ? r2(longitudUnid * f) : null;
  const cu = m.cuantificacion;
  const sup = cu?.superficie ?? null;

  let areaM2: number | undefined;
  if (conEscala && sup) {
    if (sup.fuente === 'area_explicita' && (m.area ?? 0) > 0) areaM2 = r2(m.area! * f * f);
    else if (sup.fuente === 'recintos_aprox' && (m.area_recintos ?? 0) > 0) areaM2 = r2(m.area_recintos! * f * f);
    else if (sup.fuente === 'recintos_medidos') {
      const a = m.recintos_medidos?.medidos.area_m2 ?? sup.cantidad ?? 0;
      const k = valido(factorDelLector) ? (f / factorDelLector) ** 2 : 1;
      if (a > 0) areaM2 = r2(a * k);
    }
  }
  const areaEsAprox = areaM2 !== undefined && sup?.confianza === 'aproximada';

  const corridoUnid = m.corrido ?? 0;
  const dobleTrazo = conEscala && cu?.fuente === 'eje_muro' && corridoUnid > 0 && esMuroSemantico;
  const corridoM = dobleTrazo ? r2(corridoUnid * f) : null;
  const espesorUnid = m.espesor ?? 0;
  const espesorSugerido = dobleTrazo && espesorUnid > 0 ? r3(espesorUnid * f) : undefined;
  // La nitidez del muro la juzgó el lector (`confianza`); sin su escala no pudo juzgarla: se revisa.
  const revisar = (dobleTrazo && cu?.confianza !== 'medida') || (areaEsAprox && !dobleTrazo);
  return { conEscala, longitudUnid, metros, areaM2, areaEsAprox, dobleTrazo, corridoM, espesorSugerido, revisar, baseLargo: corridoM ?? metros };
}
